/**
 * @FileDescription: 对话中心-对端展示名脱敏（与 PublisherProfilePanel / DialogueCenter 规则一致）
 * @Author: Cursor AI
 * @Date: 2026-05-19
 * @LastEditors: Cursor AI
 * @LastEditTime: 2026-05-19
 */

/**
 * 姓名脱敏：2 字为「张*」，更长为「张*总」
 * @param {string} name
 * @returns {string}
 */
export function maskPeerDisplayName(name) {
    const text = String(name || '').trim()
    if (!text) return ''
    if (text === '未命名联系人') return text
    if (text.length <= 1) return '*'
    if (text.length === 2) return `${text[0]}*`
    return `${text[0]}*${text[text.length - 1]}`
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
export function namesLikelySame(a, b) {
    const x = String(a || '').trim()
    const y = String(b || '').trim()
    if (!x || !y) return false
    if (x === y) return true
    if (x.includes(y) || y.includes(x)) return true
    const fx = [...x][0]
    const fy = [...y][0]
    if (fx && fy && fx === fy && x.length <= 8 && y.length <= 8) return true
    return false
}

/**
 * 首条消息前的占位会话：入口将联系人放在 receiverUserId，当前用户为发起方。
 * @param {object} chat
 * @param {string} currentUserId
 * @param {function} coerceId
 * @returns {boolean}
 */
export function isDraftConversationInitiator(chat, currentUserId, coerceId) {
    const me = coerceId(currentUserId)
    const rUid = coerceId(chat?.receiverUserId)
    const sUid = coerceId(chat?.senderUserId)
    return !!(me && rUid && me !== rUid && (!sUid || sUid === me))
}

/**
 * 占位会话推断对端身份类型（send/receive 表示商机双方角色，未必已写入 senderUserId）。
 * @param {object} chat
 * @param {string} currentUserId
 * @param {function} coerceId
 * @returns {string}
 */
export function inferPeerRoleTypeForDraftPlaceholder(chat, currentUserId, coerceId) {
    if (!isDraftConversationInitiator(chat, currentUserId, coerceId)) return ''
    const sendType = String(chat.sendType || '').trim().toLowerCase()
    const receiveType = String(chat.receiveType || '').trim().toLowerCase()
    if (!sendType || !receiveType) return ''

    if (sendType === receiveType) {
        return sendType
    }
    if (sendType === 'scheme' || sendType === 'supplier') {
        if (receiveType === 'company') return 'company'
    }
    if (sendType === 'company') {
        if (receiveType === 'scheme' || receiveType === 'supplier') return receiveType
    }
    if (sendType === 'company' && (receiveType === 'scheme' || receiveType === 'supplier')) {
        return 'company'
    }
    if ((sendType === 'scheme' || sendType === 'supplier') && receiveType === 'company') {
        return 'company'
    }
    return ''
}

export function isDirectVisibleByRoleForChat(chat, currentUserId, coerceId) {
    if (!chat) return false
    const sendType = String(chat.sendType || '').trim().toLowerCase()
    const receiveType = String(chat.receiveType || '').trim().toLowerCase()
    if (!sendType || !receiveType) return false

    const sendIsSs = sendType === 'scheme' || sendType === 'supplier'
    const receiveIsSs = receiveType === 'scheme' || receiveType === 'supplier'

    if (sendIsSs && receiveIsSs) return true

    const me = coerceId(currentUserId)
    const sUid = coerceId(chat.senderUserId)
    const rUid = coerceId(chat.receiverUserId)
    const isDraftSender = isDraftConversationInitiator(chat, currentUserId, coerceId)
    const isSender = !!(me && sUid && me === sUid) || isDraftSender
    const isReceiver = !!(me && rUid && me === rUid)

    if (sendType === 'company' && receiveIsSs) return isSender
    if (sendIsSs && receiveType === 'company') return isReceiver
    return false
}

/**
 * @param {object} chat
 * @param {string} currentUserId
 * @param {string} currentUserDisplayName
 * @param {function} coerceId
 * @returns {string}
 */
export function getPeerRoleType(chat, currentUserId, currentUserDisplayName, coerceId) {
    if (!chat) return ''
    const sendType = String(chat.sendType || '').trim().toLowerCase()
    const receiveType = String(chat.receiveType || '').trim().toLowerCase()
    const me = coerceId(currentUserId)
    const sUid = coerceId(chat.senderUserId)
    const rUid = coerceId(chat.receiverUserId)
    if (me && sUid && me === sUid && receiveType) return receiveType
    if (me && rUid && me === rUid && sendType) return sendType
    const myName = String(currentUserDisplayName || '').trim()
    const sName = String(chat.senderName || '').trim()
    const rName = String(chat.receiverName || '').trim()
    if (myName && rName && myName === rName && sendType) return sendType
    if (myName && sName && myName === sName && receiveType) return receiveType
    if (myName && rName && namesLikelySame(myName, rName) && sendType) return sendType
    if (myName && sName && namesLikelySame(myName, sName) && receiveType) return receiveType

    const draftPeer = inferPeerRoleTypeForDraftPlaceholder(chat, currentUserId, coerceId)
    if (draftPeer) return draftPeer
    return ''
}

/**
 * @param {object} chat
 * @param {string} currentUserId
 * @param {string} currentUserDisplayName
 * @param {function} coerceId
 * @returns {boolean}
 */
export function shouldMaskPeerDisplayForChat(chat, currentUserId, currentUserDisplayName, coerceId) {
    if (!chat || chat.profileInfoUnlocked === true) return false
    if (isDirectVisibleByRoleForChat(chat, currentUserId, coerceId)) return false
    return getPeerRoleType(chat, currentUserId, currentUserDisplayName, coerceId) === 'company'
}

/**
 * @param {string} value
 * @param {object} chat
 * @param {string} currentUserId
 * @param {string} currentUserDisplayName
 * @param {function} coerceId
 * @returns {string}
 */
export function maskFieldIfPeerCompany(value, chat, currentUserId, currentUserDisplayName, coerceId) {
    const text = String(value || '').trim()
    if (!text || text === '-') return text
    if (!shouldMaskPeerDisplayForChat(chat, currentUserId, currentUserDisplayName, coerceId)) return text
    return maskPeerDisplayName(text)
}
